import { useState } from 'react'

import isEqual from 'lodash/isEqual'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import {
  CostCenterAssignment,
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import {
  DEFAULT_ASSIGNMENT_COST_CENTER_ID,
  DEFAULT_ASSIGNMENT_PERCENTAGE,
  defaultAssignment,
} from '../../../lib/defaults'
import { useAssignmentsValidator } from './useAssignmentsValidator'
import { DateRange } from './validations/overlaps'

interface ValueAssignment {
  name: string
  id: string
  percentage: number
}

interface Props {
  currentAssignments: (FormattedAssignmentWithDeletion | FormattedAssignment)[]
  allAssignments: CostCenterAssignment[]
  defaultDateRange?: { startDate: string; endDate: string }
}

export const useAssignmentsManager = ({
  currentAssignments,
  allAssignments,
  defaultDateRange,
}: Props) => {
  const [assignmentToCreateTemplate, setAssignmentToCreateTemplate] = useState<
    FormattedAssignment[]
  >([])
  // We want to disable the button when the user has not interacted with the component
  const [userInteracted, setUserInteracted] = useState(false)
  const [assignmentsToCreate, setAssignmentsToCreate] = useState<FormattedAssignment[]>([])
  const [assignmentsToUpdate, setAssignmentsToUpdate] =
    useState<FormattedAssignmentWithDeletion[]>(currentAssignments)
  const [assignments, setAssignments] = useState<FormattedAssignment[]>([
    ...assignmentsToCreate,
    ...assignmentsToUpdate,
  ])

  const assignmentsToUpdateNotMarkedForDeletion = assignmentsToUpdate.filter(
    (v) => !v.markedForDeletion
  )

  useUnsafeEffect(() => {
    if (userInteracted) return

    const assignmentsAreDifferent = !isEqual(assignments, [
      ...assignmentsToCreate,
      ...assignmentsToUpdate,
    ])

    if (assignmentsAreDifferent) {
      setUserInteracted(true)
    }
  }, [assignments, userInteracted, assignmentsToCreate, assignmentsToUpdate])

  //------------ Date Range ------------

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: currentAssignments[0]?.startDate || defaultDateRange?.startDate || '',
    endDate: currentAssignments[0]?.endDate || defaultDateRange?.endDate || '',
  })

  //------------ Validations ------------

  const { validForSave, validations } = useAssignmentsValidator({
    selectedDateRange,
    currentAssignments: currentAssignments,
    displayedAssignments: assignments,
    allAssignments,
    userInteracted: userInteracted,
  })

  //------------ Date Range Management ------------

  const setNewDateRange = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    const dateToAssign = {
      ...(startDate ? { startDate } : {}),
      ...(typeof endDate === 'string' ? { endDate } : {}),
    }

    setSelectedDateRange((prevDateRange) => ({
      ...prevDateRange,
      ...dateToAssign,
    }))

    setAssignmentToCreateTemplate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToCreate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToUpdate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })
  }

  //------------ Add New Assignment ------------

  const addNewAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
      previousCostCenterId: Math.min(...assignments.map((item) => Number(item.costCenterId))),
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Delete Assignment ------------

  const deleteValue = (value: ValueAssignment) => {
    const storedAssignment = assignmentsToUpdate.find((v) => String(v.costCenterId) === value.id)
    const assignmentInCreate = assignmentsToCreate.find((v) => String(v.costCenterId) === value.id)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(value.id)),
      ])
    }

    setAssignments([...assignments.filter((v) => String(v.costCenterId) !== value.id)])
  }

  const manageCostcenterInAssignmentToCreateInTemplate = (
    value: FormattedAssignment,
    newValue: ValueAssignment
  ) => {
    const { costCenterId } = value

    if (
      newValue.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE ||
      Number(newValue.id) <= DEFAULT_ASSIGNMENT_COST_CENTER_ID
    ) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    } else {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
      ])

      setAssignmentsToCreate([
        ...assignmentsToCreate,
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }
  }

  //------------ Update Assignment ------------

  const updateAssignment = (valueId: string, newValue: ValueAssignment) => {
    const assignment = assignments.find((v) => v.costCenterId == Number(valueId))

    if (!assignment) return

    const costCenterInUpdateAssignments = assignmentsToUpdateNotMarkedForDeletion.find(
      (v) => v.costCenterId == Number(valueId)
    )

    const costCenterInAssignmentToCreateInTemplate = assignmentToCreateTemplate.find(
      (v) => v.costCenterId == Number(valueId)
    )

    if (costCenterInUpdateAssignments) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== costCenterInUpdateAssignments.id),
        {
          ...costCenterInUpdateAssignments,
          percentage: newValue.percentage,
          costCenterId: Number(newValue.id),
        },
      ])
    } else if (costCenterInAssignmentToCreateInTemplate) {
      manageCostcenterInAssignmentToCreateInTemplate(
        costCenterInAssignmentToCreateInTemplate,
        newValue
      )
    } else {
      const assignmentToPatch = assignmentsToCreate.find((v) => v.costCenterId == Number(valueId))

      if (!assignmentToPatch) return

      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(newValue.id)),
        { ...assignmentToPatch, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }

    // In order to not to update the order of the assignments, we need to map the assignments and
    //update the assignment with the new value
    setAssignments(
      assignments.map((item) =>
        String(item.costCenterId) === valueId
          ? {
              ...item,
              ...newValue,
              costCenterId: Number(newValue.id),
            }
          : item
      )
    )
  }

  return {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments,
    validForSave,
    validations,
    addNewAssignment,
    deleteValue,
    updateAssignment,
    setAssignmentsToCreate,
    setAssignmentsToUpdate,
    setNewDateRange,
  }
}
